/**
 * Modern equivalent of cs-core's {@code utilities/randomizer/Randomizer.ts}.
 * Default impl wraps {@code Math.random}; tests can pass {@link PredeterminedRandom}
 * to make spin outcomes deterministic.
 */
export interface Random {
  nextBoolean(): boolean;
  /** Inclusive lower, exclusive upper. */
  nextIntBetween(minInclusive: number, maxExclusive: number): number;
  nextFloat(): number;
  /** Pick a uniform-random element. */
  chooseOne<T>(arr: readonly T[]): T;
}

export class DefaultRandom implements Random {
  nextBoolean(): boolean {
    return Math.random() < 0.5;
  }

  nextIntBetween(minInclusive: number, maxExclusive: number): number {
    if (maxExclusive <= minInclusive) {
      throw new Error(`maxExclusive (${maxExclusive}) must be > minInclusive (${minInclusive})`);
    }
    return Math.floor(Math.random() * (maxExclusive - minInclusive)) + minInclusive;
  }

  nextFloat(): number {
    return Math.random();
  }

  chooseOne<T>(arr: readonly T[]): T {
    if (arr.length === 0) {
      throw new Error('chooseOne: empty array');
    }
    return arr[this.nextIntBetween(0, arr.length)] as T;
  }
}

/**
 * Lifted in spirit from cs-core's PredeterminedRandomizer — used for tests
 * and demos where a specific spin outcome must be reproducible.
 */
export class PredeterminedRandom implements Random {
  private cursor = 0;
  constructor(private readonly values: readonly number[]) {}

  private next(): number {
    if (this.values.length === 0) {
      throw new Error('PredeterminedRandom has no values');
    }
    const v = this.values[this.cursor % this.values.length] as number;
    this.cursor++;
    return v;
  }

  nextBoolean(): boolean {
    return this.next() >= 0.5;
  }

  nextIntBetween(minInclusive: number, maxExclusive: number): number {
    if (maxExclusive <= minInclusive) {
      throw new Error(`maxExclusive (${maxExclusive}) must be > minInclusive (${minInclusive})`);
    }
    const v = this.next();
    return Math.floor(v * (maxExclusive - minInclusive)) + minInclusive;
  }

  nextFloat(): number {
    return this.next();
  }

  chooseOne<T>(arr: readonly T[]): T {
    if (arr.length === 0) {
      throw new Error('chooseOne: empty array');
    }
    return arr[this.nextIntBetween(0, arr.length)] as T;
  }
}
